<template>
  <div class="km__modules__color">
    <ColorButtons
        @backbutton_clicked="closeOneLayer"
        @closebutton_clicked="closeLayer"
    />
    <ColorAreaColorSelection
        :visibleAreaColorObjects="visibleColorObjectsExcludingOutlines"
        :currentColorCode="getCurrentColorCode"
        @colorpicker_clicked="areaColorCodeGroupingClicked"
        @setCurrentAreaColorObject="setActiveAreaColorObject"
    />
    <ColorAreaOutlinePatternSelect
        :activeAreaColorObject="activeAreaColorObject"
        :outlineForActiveAreaColorObject="outlineColorObjectForCurrentColorObject"
        @setColorObjectToEdit="setColorObjectToEditFunction"
        @openpatterncolors="openpatterncolors"
        @openpatterncategories="openpatterncategories"
        :may_have_pattern="colorCodeMayHavePattern"
    />
    <ColorShadeGroupSelection
        containerclass='km__modules__color--shadegroup-selection'
        :currentColorValueIntOrNONEstring="currentColorValueIntOrNONEstring"
        :palettenCluster="currentPalettenClusterSorted"
        :animationkeyframesgetter="shadeGroupSelectionAnimation"
        :currentpalette="currentPalette"
        @setColor="setColor"
    />
    <ColorShadeSelection
        containerclass="km__modules__color--shade"
        :currentColorValueIntOrNONEstring="currentColorValueIntOrNONEstring"
        :currentpalette="currentPalette"
        :palettenCluster="currentPalettenClusterSorted"
        :animationkeyframesgetter="shadeSelectionAnimation"
        @setColor="setColor"
    />
    <ModulePattern :currentColorCode="getCurrentColorCode"/>
    <PatternEdit :currentColorCode="getCurrentColorCode"/>

  </div>
</template>

<script lang="ts" setup>
import ColorButtons from "./ColorButtons.vue";
import ColorAreaColorSelection from "./ColorAreaColorSelection.vue";
import ColorAreaOutlinePatternSelect from "./ColorAreaOutlinePatternSelect.vue";
import ColorShadeSelection from "./ColorShadeSelection.vue";

import ModulePattern from "@km/components/patternselection/ModulePattern.vue";
import PatternEdit from "@km/components/patternedit/PatternEdit.vue";
import ColorShadeGroupSelection from "@km/components/color/ColorShadeGroupSelection.vue";
import {useStore} from "vuex";
import {computed} from "vue";
import {Color} from "@k/js/colorObject";
import {EventListener, EventVerteiler} from "@k/global/eventVerteiler";

import {ColorViewDataWithOutlineAndPatterns} from "@km/components/color/ColorViewDataWithOutlineAndPatterns";
import {ColorAnimationVerwalter} from "@km/components/color/ColorAnimationVerwalter";
import {COLORMODULE_POSSIBLE_STATES} from "@km/components/color/ModuleColorAnimationStates";
import {PATTERN_EVENTNAMES} from "@k/js/pattern/patternFactory";
import {TabNames} from "@km/data/tabs";
import {ColorUtilsVue} from "@km/js/ColorUtilsVue";
import {useColoreditStore} from "@km/components/color/colorEditStatusStore";
import {useModuleColorOpenStateStore} from "@km/store/frontendstate/useModuleColorOpenState";
import {closeAllLayersDownToAreaColorGroupings} from "@km/components/color/closeColorLayers";



const store = useStore();
const statusStore = useColoreditStore();
const eventListener = new EventListener()
    .addEventListener("initColorsEvent", data => statusStore.initColorsEventHandler(store, data.colorCodes))
    .addEventListener("colorSelectedEvent", ({colorCode}) => colorSelectedEventHandler(colorCode));

new ColorAnimationVerwalter(store);

function colorSelectedEventHandler(colorCode: string) {
  const statusStore = useColoreditStore();
  const color = statusStore.colorCodes.find(c => c.colorCode === colorCode);
  if (color === undefined || !color.isVisible) {
    ColorAnimationVerwalter.setModuleColorState(COLORMODULE_POSSIBLE_STATES.areaColorGroupSelection)
    return;
  }
  statusStore.setColorObjectToEdit(colorCode);
  if (statusStore.colorObjectToEdit === null) {
    return;
  }
  statusStore.setActiveAreaColorObject(store.getters.getColorObjectByColorCode(colorCode.replace("_O", "")));
  const pattern = store.getters.getPattern(colorCode);
  if (pattern !== null) {
    EventVerteiler.broadcast(PATTERN_EVENTNAMES.SELECTED, pattern);
    ColorAnimationVerwalter.setModuleColorState(COLORMODULE_POSSIBLE_STATES.patterncolors);
  } else {
    ColorAnimationVerwalter.setModuleColorState(COLORMODULE_POSSIBLE_STATES.individualColorSelection);
  }
  store.commit("SET_TAB_OPEN", TabNames.COLORS);
}


function setColor(colorObj: { RGBHex, quellPalette }) {
  const statusStore = useColoreditStore();
  const palette = ColorUtilsVue.getPalettenNameFromPalettenClusterWithColorHexValue(statusStore.currentPalettenCluster, colorObj.RGBHex, colorObj.quellPalette);
  EventVerteiler.broadcast("colorChangedInColorPicker", {
    colorCode: (statusStore.colorObjectToEdit as Color).colorCode,
    value: colorObj.RGBHex,
    palette,
    byUser: true
  });
}


const areaColorCodeGroupingClicked = (colorObject: ColorViewDataWithOutlineAndPatterns) => {
  const statusStore = useColoreditStore();
  statusStore.setActiveAreaColorObject(statusStore.getColor(colorObject.colorCode) as Color);
  ColorAnimationVerwalter.setModuleColorState(COLORMODULE_POSSIBLE_STATES.areaOutlinePatternSelection);
  statusStore.setColorObjectToEdit(statusStore.activeAreaColorObject?.colorCode || null);
}

const closeOneLayer = () => ColorAnimationVerwalter.closeOneLayer();
const closeLayer = () => closeAllLayersDownToAreaColorGroupings();


const openStatusStore = useModuleColorOpenStateStore();
const openpatterncolors = () => {
  openStatusStore.setCurrentState(COLORMODULE_POSSIBLE_STATES.patterncolors);
};


const openpatterncategories = () => {
  openStatusStore.setCurrentState(COLORMODULE_POSSIBLE_STATES.patterncategories);
};
const colorCodeMayHavePattern = ccode => store.getters["ModelStore/getCurrentOwayoProduct"]?.colorCodes.find(c => c.colorCode === ccode)?.ImKonfiKoennenPatternsVerwendetWerden || false

const patternSelectIsOpen = computed(() => openStatusStore.getCurrentState === COLORMODULE_POSSIBLE_STATES.patterncategories);
const shadeGroupSelectionAnimation = ColorAnimationVerwalter.getColorShadeGroupAnimation();
const shadeSelectionAnimation = ColorAnimationVerwalter.getColorShadeAnimation();
const visibleColorObjectsExcludingOutlines = computed(()=>statusStore.visibleColorObjectsExcludingOutlines);
const activeAreaColorObject = computed(()=>statusStore.activeAreaColorObject);
const outlineColorObjectForCurrentColorObject = computed(()=>statusStore.getOutlineForActiveAreaColorObject);
const setColorObjectToEditFunction = (ccode: string) => {
  statusStore.setColorObjectToEdit(ccode);
  ColorAnimationVerwalter.setModuleColorState(COLORMODULE_POSSIBLE_STATES.individualColorSelection);
}
const currentColorValueIntOrNONEstring = computed(()=>statusStore.currentColorValueIntOrNONEstring);
const currentPalette = computed(()=>statusStore.currentPalette);
const currentPalettenClusterSorted = computed(()=>statusStore.currentPalettenClusterSorted);
const getCurrentColorCode = computed(()=>statusStore.getCurrentColorCode);
const setActiveAreaColorObject = (c: Color) => statusStore.setActiveAreaColorObject(c);
</script>

